import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { Activity, Loader2, Mail, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";

export default function Register() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  // Phase 8C — After successful register, show a "check your email" screen
  const [pending, setPending] = useState(null);  // { email, dev_mode_verify_url? }
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setLoading(true);
    try {
      const r = await api.post("/auth/register", { name: name.trim(), email: email.trim(), password });
      if (r.data.requires_verification === false && r.data.token) {
        // Admin path — got JWT immediately
        toast.success("Account created — welcome");
        navigate("/dashboard", { replace: true });
      } else {
        // Regular user path — check email screen
        setPending({ email: r.data.email || email.trim(), dev_mode_verify_url: r.data.dev_mode_verify_url });
        toast.success("Check your email to verify your account");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      const r = await api.post("/auth/resend-verification", { email: pending.email });
      toast.success(r.data.message || "Sent");
      if (r.data.dev_mode_verify_url) {
        setPending((p) => ({ ...p, dev_mode_verify_url: r.data.dev_mode_verify_url }));
      }
      setResendCooldown(60);
      const t = setInterval(() => setResendCooldown((c) => c <= 1 ? (clearInterval(t), 0) : c - 1), 1000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to resend");
    } finally { setResending(false); }
  };

  return (
    <div className="min-h-screen flex bg-[#0a0e1a]">
      <div className="hidden lg:flex flex-col justify-between w-1/2 p-12 border-r border-[#1f2937] vx-grid-bg">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-[#3b82f6]/10 border border-[#3b82f6]/40 flex items-center justify-center">
            <Activity className="w-5 h-5 text-[#3b82f6]" strokeWidth={2} />
          </div>
          <div>
            <div className="font-heading font-black text-2xl tracking-tighter leading-none">VEEROX</div>
            <div className="vx-label mt-1">Autonomous Trading Intelligence</div>
          </div>
        </div>
        <div>
          <h1 className="font-heading text-4xl xl:text-5xl font-black tracking-tighter leading-[1.05] mb-6">
            Trade with a<br/><span className="text-[#3b82f6]">Trading OS.</span>
          </h1>
          <p className="text-[#9ca3af] max-w-md leading-relaxed">
            9 pillars of intelligence protecting your capital: Market Regime · Strategy Selection ·
            Risk-First AI · Health Score · Coach · Portfolio Optimizer · Prediction.
            Register in 30 seconds and connect your MT5.
          </p>
          <p className="text-[#6b7280] italic max-w-md text-sm mt-4 leading-relaxed border-l-2 border-[#3b82f6]/40 pl-3">
            &ldquo;We don&apos;t optimize for winning trades. We optimize for surviving markets.&rdquo;
          </p>
        </div>
        <div className="text-[10px] text-[#6b7280] font-mono">© 2026 Veerox Labs</div>
      </div>
      <div className="flex-1 flex items-center justify-center p-8">
        <form
          onSubmit={submit}
          className="w-full max-w-sm space-y-6"
          data-testid="register-form"
        >
          <div>
            <div className="vx-label mb-2">Create account</div>
            <h2 className="font-heading text-3xl font-black tracking-tighter">Join the terminal</h2>
            <p className="text-sm text-[#9ca3af] mt-2">
              Already have one?{" "}
              <Link to="/login" className="text-[#3b82f6] hover:underline" data-testid="link-login">
                Sign in
              </Link>
            </p>
          </div>
          {pending ? (
            <div className="space-y-4" data-testid="check-email-screen">
              <div className="rounded-lg border border-[#10b981]/40 bg-[#10b981]/5 p-5">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#10b981] shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-sm mb-1">Check your email</div>
                    <div className="text-xs text-[#9ca3af] font-mono leading-relaxed">
                      We&apos;ve sent a verification link to <span className="text-[#e5e7eb]">{pending.email}</span>.
                      Click the link within 24 hours to activate your account.
                      Also check your spam folder.
                    </div>
                  </div>
                </div>
              </div>
              {pending.dev_mode_verify_url && (
                <div className="rounded border border-[#fbbf24]/40 bg-[#fbbf24]/10 p-3 text-[10px] font-mono text-[#fbbf24]" data-testid="dev-verify-url">
                  DEV mode (SendGrid unconfigured): <a href={pending.dev_mode_verify_url}
                     className="text-[#3b82f6] break-all underline">{pending.dev_mode_verify_url}</a>
                </div>
              )}
              <div className="flex flex-col gap-2 pt-2">
                <button
                  type="button"
                  onClick={resend}
                  disabled={resending || resendCooldown > 0}
                  data-testid="resend-verification-btn"
                  className="h-10 rounded-md border border-[#1f2937] hover:bg-[#1f2937]/40 text-xs font-mono uppercase tracking-widest disabled:opacity-40"
                >
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s`
                    : resending ? "Sending…"
                    : "Resend verification email"}
                </button>
                <Link to="/login" className="text-center text-xs text-[#9ca3af] hover:text-[#3b82f6]" data-testid="back-to-login">
                  ← Back to sign in
                </Link>
              </div>
            </div>
          ) : (
          <>
          <div className="space-y-4" data-testid="register-form-fields">
            <div>
              <label className="vx-label block mb-1.5">Name</label>
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="vx-input"
                data-testid="register-name-input"
                autoComplete="name"
              />
            </div>
            <div>
              <label className="vx-label block mb-1.5">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="vx-input"
                data-testid="register-email-input"
                autoComplete="email"
              />
            </div>
            <div>
              <label className="vx-label block mb-1.5">Password (min 8 chars)</label>
              <input
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="vx-input"
                data-testid="register-password-input"
                autoComplete="new-password"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="register-submit-btn"
            className="w-full h-11 rounded-md bg-[#3b82f6] hover:bg-[#2563eb] transition-colors font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60 active:scale-[0.98]"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {loading ? "Creating…" : "Create account"}
          </button>
          </>
          )}
        </form>
      </div>
      <Toaster theme="dark" position="top-right" toastOptions={{
        style: { background: "#111827", color: "#e5e7eb", border: "1px solid #1f2937" }
      }} />
    </div>
  );
}
